import { useEffect, useState } from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Avatar,
  Box,
  Button,
  Chip,
  IconButton,
  Skeleton,
  Tab,
  Tabs,
  TablePagination,
  Typography,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import UnfoldMoreIcon from "@mui/icons-material/UnfoldMore";
import UnfoldLessIcon from "@mui/icons-material/UnfoldLess";
import httpclient from "../../../../Utils";
import { useNotification } from "../../../../Hooks/useNotification";
import { useApiErrorHandler } from "../../../../Hooks/useApiErrorHandler";
import useTokenRefresh from "../../../../Hooks/useTokenRefresh";

import FilterBtn from "../../../../Assets/Icons/btn-filters.png";
import FilterBtnUp from "../../../../Assets/Icons/btn-filters-up.png";
import Filter from "./Filter";
import { INITIAL_FILTER_STATE, buildFlatFilterParams } from "./orderFilterConfig";

// "Overview" (non-today) defaults to a 7-day Invoice Date range.
// "Delivered" (non-today) defaults to a 7-day Delivered Date range instead.
const shouldDefaultInvoiceDate = (tab, isTodayPage) =>
  !isTodayPage && tab === "overview";
const shouldDefaultDeliveredDate = (tab, isTodayPage) =>
  !isTodayPage && tab === "delivered";

// Order & display labels for the AM / MID / PM job-period tabs. "M" is the
// API's key for the midday bucket.
const JOB_PERIODS = [
  { key: "AM", label: "AM" },
  { key: "M", label: "MID" },
  { key: "PM", label: "PM" },
];

const getDefault7DayRange = () => {
  const endDate = new Date();
  const startDate = new Date();
  startDate.setDate(endDate.getDate() - 7);
  const formatDate = (value) => value.toISOString().split("T")[0];
  return { start: formatDate(startDate), end: formatDate(endDate) };
};

const DriversPanel = ({
  activeTab,
  isToday = false,
  renderExpandedContent,
  onAllowAllInvoiceTabChange,
  onStatuswiseCountChange,
}) => {
  const driverListEndpoint = isToday
    ? "delivery/today-job-driver-list"
    : "delivery/job-driver-list";

  const [drivers, setDrivers] = useState([]);
  const [driverFilterList, setDriverFilterList] = useState([]);
  const [orderStatuses, setOrderStatuses] = useState([]);
  const [totalJobs, setTotalJobs] = useState(0);
  const [totalStops, setTotalStops] = useState(0);
  const [totalLines, setTotalLines] = useState(0);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [totalDrivers, setTotalDrivers] = useState(0);
  const [expandedDriverIds, setExpandedDriverIds] = useState([]);
  const [periodByDriver, setPeriodByDriver] = useState({});
  const [filterAnchorEl, setFilterAnchorEl] = useState(null);
  const [filterData, setFilterData] = useState(INITIAL_FILTER_STATE);
  const [submittedFilters, setSubmittedFilters] = useState({});
  // True once the user has explicitly removed/cleared the default Invoice
  // Date filter during this tab visit - prevents it from reappearing until
  // the tab actually changes again.
  const [defaultDismissed, setDefaultDismissed] = useState(false);

  const showNotification = useNotification();
  const handleApiError = useApiErrorHandler();
  const { getTokenRefreshed: refresh } = useTokenRefresh();

  // "Overview" defaults to a 7-day Invoice Date range; "Delivered" defaults
  // to a 7-day Delivered Date range instead - and only while the user
  // hasn't explicitly set or removed one this visit. Computed at render
  // time (not via an effect) and merged into submittedFilters only for
  // display/fetch - never mutates submittedFilters itself - so there is
  // only ever ONE request per state, never an intermediate unfiltered one
  // that flashes stale totals before the filtered result replaces it.
  const hasManualInvoiceDate = Boolean(
    submittedFilters.invoiceDateStart || submittedFilters.invoiceDateEnd
  );
  const hasManualDeliveredDate = Boolean(
    submittedFilters.deliveredDateStart || submittedFilters.deliveredDateEnd
  );
  const applyDefaultInvoiceDate =
    !defaultDismissed && !hasManualInvoiceDate && shouldDefaultInvoiceDate(activeTab, isToday);
  const applyDefaultDeliveredDate =
    !defaultDismissed &&
    !hasManualDeliveredDate &&
    shouldDefaultDeliveredDate(activeTab, isToday);

  let effectiveFilters = submittedFilters;
  if (applyDefaultInvoiceDate) {
    const { start, end } = getDefault7DayRange();
    effectiveFilters = { ...effectiveFilters, invoiceDateStart: start, invoiceDateEnd: end };
  }
  if (applyDefaultDeliveredDate) {
    const { start, end } = getDefault7DayRange();
    effectiveFilters = { ...effectiveFilters, deliveredDateStart: start, deliveredDateEnd: end };
  }

  useEffect(() => {
    setPage(1);
    setExpandedDriverIds([]);
    setDefaultDismissed(false);
  }, [activeTab, isToday]);

  // Driver list used by the Filter panel's "Driver" dropdown (separate from
  // the paginated accordion list below)
  useEffect(() => {
    if (!activeTab) return;

    const fetchDriverFilterList = async () => {
      try {
        const { data } = await httpclient.get(
          `${driverListEndpoint}/${activeTab}?page=1&pagination=1000`
        );

        if (data.status === 200) {
          setDriverFilterList(data.driver_list || []);
          // Deliberately NOT calling onAllowAllInvoiceTabChange/
          // onStatuswiseCountChange here - this call is always unfiltered
          // (it only exists to populate the Driver dropdown options), so it
          // must never be a source of truth for counts. Only the
          // filter-aware fetchDrivers effect below drives those callbacks,
          // otherwise this unfiltered response can race past a filtered
          // one and overwrite it with the wrong (unfiltered) totals.
        }
      } catch (error) {
        if (error.response?.status === 401) {
          refresh();
        }
      }
    };

    fetchDriverFilterList();
  }, [activeTab, driverListEndpoint]);

  useEffect(() => {
    if (!activeTab) return;

    // Defensive guard against any rapid tab/page flips firing overlapping
    // requests - ignore a response if a newer request has since superseded it.
    let isStale = false;

    const fetchDrivers = async () => {
      setLoading(true);
      try {
        const flatFilters = buildFlatFilterParams(effectiveFilters);
        const apiUrl = `${driverListEndpoint}/${activeTab}?page=${page}&pagination=${pageSize}${
          flatFilters ? `&${flatFilters}` : ""
        }`;

        const { data } = await httpclient.get(apiUrl);
        if (isStale) return;

        if (data.status === 200) {
          setDrivers(data.data || []);
          setTotalJobs(data.total_jobs || 0);
          setTotalStops(data.total_stops || 0);
          setTotalLines(data.total_lines || 0);
          setTotalDrivers(data.meta?.total ?? data.count ?? (data.data || []).length);

          if (data.allow_all_invoice_tab_enabled !== undefined) {
            onAllowAllInvoiceTabChange?.(data.allow_all_invoice_tab_enabled === 1);
          }

          if (data.statuswise_job_count) {
            onStatuswiseCountChange?.(data.statuswise_job_count);
          }

          if (data.orderStatus) {
            setOrderStatuses(data.orderStatus);
          }
        } else {
          showNotification("error", data.message || "Failed to fetch drivers");
        }
      } catch (error) {
        if (isStale) return;
        if (error.response?.status === 401) {
          refresh();
        } else {
          handleApiError(error);
        }
      } finally {
        if (!isStale) setLoading(false);
      }
    };

    fetchDrivers();

    return () => {
      isStale = true;
    };
  }, [activeTab, page, pageSize, driverListEndpoint, submittedFilters, defaultDismissed]);

  // Fetches a single driver's next/prev page (or a different page size) of
  // jobs for one AM/M/PM period, since a driver's associated_job_list only
  // ever arrives with the first page of each period embedded. The API scopes
  // both the page and the page size per-driver-per-period, via
  // driver_order_page_{driver_id}_{period} and
  // driver_order_per_page_{driver_id}_{period} (e.g. _am, _pm) on the same
  // driver-list endpoint - confirmed by the page_parameter/per_page_parameter
  // fields the API echoes back in each period's pagination block.
  const fetchDriverJobsPage = async (targetDriver, period, jobPage, perPage) => {
    try {
      const flatFilters = buildFlatFilterParams(effectiveFilters);
      const periodKey = period.toLowerCase();
      const apiUrl = `${driverListEndpoint}/${activeTab}?page=${page}&pagination=${pageSize}${
        flatFilters ? `&${flatFilters}` : ""
      }&driver_order_page_${targetDriver.driver_id}_${periodKey}=${jobPage}&driver_order_per_page_${targetDriver.driver_id}_${periodKey}=${perPage}`;

      const { data } = await httpclient.get(apiUrl);
      if (data.status === 200) {
        const updatedDriver = (data.data || []).find(
          (d) => d.driver_id === targetDriver.driver_id
        );
        const updatedJobList = updatedDriver?.associated_job_list || null;

        if (updatedJobList) {
          // Keep the shared drivers state in sync too, since the AM/M/PM
          // tab counts on the collapsed bar are derived from
          // driver.associated_job_list, not from DriverJobsTable's own copy.
          setDrivers((prev) =>
            prev.map((d) =>
              d.driver_id === targetDriver.driver_id
                ? { ...d, associated_job_list: updatedJobList }
                : d
            )
          );
        }

        return updatedJobList;
      }
      showNotification("error", data.message || "Failed to fetch jobs");
    } catch (error) {
      if (error.response?.status === 401) {
        refresh();
      } else {
        handleApiError(error);
      }
    }
    return null;
  };

  const getActivePeriod = (driver) => {
    const selected = periodByDriver[driver.driver_id];
    if (selected) return selected;
    const periodsData = driver.associated_job_list?.data || {};
    const firstNonEmpty = JOB_PERIODS.find(
      (p) => (periodsData[p.key] || []).length > 0
    );
    return firstNonEmpty?.key || "AM";
  };

  const handlePeriodChange = (driverId) => (event, newPeriod) => {
    event.stopPropagation();
    setPeriodByDriver((prev) => ({ ...prev, [driverId]: newPeriod }));
  };

  const handleAccordionChange = (driver) => (event, isExpanded) => {
    setExpandedDriverIds((prev) =>
      isExpanded
        ? [...prev, driver.driver_id]
        : prev.filter((id) => id !== driver.driver_id)
    );
  };

  const handleChangeFilter = (field) => (event, value) => {
    setFilterData((prev) => ({
      ...prev,
      [field]: value || (event && event.target.value) || "",
    }));
  };

  const handleFilterToggle = (event) => {
    const target = event.currentTarget;
    setFilterAnchorEl((prev) => {
      if (prev) return null;
      // Pre-fill the form with whatever is currently shown (including the
      // default, if active) so opening the panel doesn't look empty.
      setFilterData((prevData) => ({
        ...prevData,
        invoiceDateStart: effectiveFilters.invoiceDateStart || "",
        invoiceDateEnd: effectiveFilters.invoiceDateEnd || "",
        deliveredDateStart: effectiveFilters.deliveredDateStart || "",
        deliveredDateEnd: effectiveFilters.deliveredDateEnd || "",
        status: effectiveFilters.status || "",
      }));
      return target;
    });
  };

  const handleFilterClose = () => {
    setFilterAnchorEl(null);
  };

  const handleFilterApply = () => {
    setSubmittedFilters(filterData);
    setPage(1);
    setFilterAnchorEl(null);
  };

  const handleRemoveFilter = (field) => {
    if (
      (field === "invoiceDateStart" || field === "invoiceDateEnd") &&
      !hasManualInvoiceDate &&
      applyDefaultInvoiceDate
    ) {
      // Currently showing the default (not a user-set value) - dismiss it
      // for the rest of this tab visit instead of deleting a key that was
      // never actually in submittedFilters.
      setDefaultDismissed(true);
      setFilterData((prev) => ({ ...prev, invoiceDateStart: "", invoiceDateEnd: "" }));
      setPage(1);
      return;
    }

    if (
      (field === "deliveredDateStart" || field === "deliveredDateEnd") &&
      !hasManualDeliveredDate &&
      applyDefaultDeliveredDate
    ) {
      setDefaultDismissed(true);
      setFilterData((prev) => ({ ...prev, deliveredDateStart: "", deliveredDateEnd: "" }));
      setPage(1);
      return;
    }

    setFilterData((prev) => ({ ...prev, [field]: "" }));
    setSubmittedFilters((prev) => {
      const next = { ...prev };
      delete next[field];
      return next;
    });
    setPage(1);
  };

  const handleClearFilters = () => {
    setFilterData(INITIAL_FILTER_STATE);
    setSubmittedFilters({});
    setDefaultDismissed(true);
    setPage(1);
  };

  const allCurrentExpanded =
    drivers.length > 0 &&
    drivers.every((driver) => expandedDriverIds.includes(driver.driver_id));

  const handleToggleExpandAll = () => {
    const currentIds = drivers.map((driver) => driver.driver_id);

    if (allCurrentExpanded) {
      setExpandedDriverIds((prev) => prev.filter((id) => !currentIds.includes(id)));
    } else {
      setExpandedDriverIds((prev) => [...new Set([...prev, ...currentIds])]);
    }
  };

  return (
    <Box sx={{ mb: 3 }}>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          flexWrap: "wrap",
          gap: 1.5,
          mb: 1.5,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
            References
          </Typography>
          <Chip
            size="small"
            label={totalJobs}
            sx={{
              backgroundColor: "#d6d6d6",
              color: "#242424",
              fontWeight: 600,
              height: 24,
            }}
          />
        </Box>

        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <Button
            size="small"
            variant="outlined"
            onClick={handleToggleExpandAll}
            disabled={drivers.length === 0}
            startIcon={allCurrentExpanded ? <UnfoldLessIcon /> : <UnfoldMoreIcon />}
            sx={{
              textTransform: "none",
              borderColor: "#D7DCE3",
              color: "#48505E",
              "&:hover": {
                borderColor: "#48505E",
                backgroundColor: "#F5F5F5",
              },
            }}
          >
            {allCurrentExpanded ? "Collapse All" : "Expand All"}
          </Button>

          <IconButton
            onClick={handleFilterToggle}
            size="small"
            sx={{ p: 0.5 }}
          >
            <Box
              component="img"
              src={filterAnchorEl ? FilterBtnUp : FilterBtn}
              alt="Filter"
            />
          </IconButton>
        </Box>
      </Box>

      <Filter
        filterOpen={Boolean(filterAnchorEl)}
        filterAnchorEl={filterAnchorEl}
        onCloseFilter={handleFilterClose}
        filterData={filterData}
        handleChangeFilter={handleChangeFilter}
        handleFilter={handleFilterApply}
        handleClearFilters={handleClearFilters}
        handleRemove={handleRemoveFilter}
        submittedData={effectiveFilters}
        drivers={driverFilterList}
        activeTab={activeTab}
        orderStatuses={orderStatuses}
      />

      {loading ? (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
          {Array.from({ length: 5 }).map((_, index) => (
            <Skeleton key={index} variant="rounded" height={52} />
          ))}
        </Box>
      ) : (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
          {drivers.map((driver) => {
            const isExpanded = expandedDriverIds.includes(driver.driver_id);
            const activePeriod = getActivePeriod(driver);
            const periodsData = driver.associated_job_list?.data || {};
            const periodsPagination = driver.associated_job_list?.pagination || {};
            const periodsTotal = driver.associated_job_list?.total || {};

            return (
              <Accordion
                key={driver.driver_id}
                expanded={isExpanded}
                onChange={handleAccordionChange(driver)}
                disableGutters
                TransitionProps={{
                  unmountOnExit: true,
                  mountOnEnter: true,
                  timeout: 300,
                }}
                sx={{
                  border: isExpanded ? "2px solid #48505E" : "1px solid #D7DCE3",
                  borderRadius: "8px !important",
                  boxShadow: "none",
                  overflow: "hidden",
                  transition: "border-color 300ms ease",
                  "&:before": {
                    display: "none",
                  },
                }}
              >
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon />}
                  sx={{
                    backgroundColor: isExpanded ? "#EEF0F4" : "#fff",
                    transition: "background-color 300ms ease",
                    "&.Mui-expanded": {
                      minHeight: 48,
                    },
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      width: "100%",
                      pr: 1,
                      flexWrap: "wrap",
                      gap: 1,
                    }}
                  >
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                      <Avatar sx={{ width: 32, height: 32, fontSize: "14px" }}>
                        {driver.full_name?.trim()?.charAt(0)?.toUpperCase() || "?"}
                      </Avatar>
                      <Typography
                        sx={{
                          fontSize: "14px",
                          fontWeight: isExpanded ? 600 : 500,
                          color: "#48505E",
                          transition: "color 300ms ease",
                        }}
                      >
                        {driver.full_name}
                      </Typography>
                      {JOB_PERIODS.map((period) => {
                        const periodTotal = periodsTotal[period.key] || {};
                        const stops = periodTotal.total_stops ?? 0;
                        const jobs = periodTotal.total_jobs ?? 0;
                        const lines = periodTotal.total_lines ?? 0;

                        if (!stops && !jobs && !lines) {
                          return null;
                        }

                        const parts = [];
                        if (stops) {
                          parts.push(
                            `${stops} Drop${stops === 1 ? "" : "s"}/Stop${stops === 1 ? "" : "s"}`
                          );
                        }
                        parts.push(`${jobs} Reference${jobs === 1 ? "" : "s"}`);
                        parts.push(`${lines} Line${lines === 1 ? "" : "s"}`);

                        return (
                          <Chip
                            key={period.key}
                            size="small"
                            label={`${period.label} - ${parts.join(", ")}`}
                            sx={{
                              backgroundColor: isExpanded ? "#48505E" : "#EEF0F4",
                              color: isExpanded ? "#fff" : "#48505E",
                              fontWeight: 600,
                              height: 24,
                              transition: "background-color 300ms ease, color 300ms ease",
                            }}
                          />
                        );
                      })}
                    </Box>

                    <Tabs
                      value={activePeriod}
                      onChange={handlePeriodChange(driver.driver_id)}
                      onClick={(event) => event.stopPropagation()}
                      variant="standard"
                      sx={{
                        minHeight: 32,
                        "& .MuiTabs-indicator": { backgroundColor: "#48505E" },
                      }}
                    >
                      {JOB_PERIODS.map((period) => {
                        const periodCount =
                          periodsPagination[period.key]?.total ??
                          (periodsData[period.key] || []).length;

                        if (!periodCount) {
                          return null;
                        }

                        return (
                          <Tab
                            key={period.key}
                            value={period.key}
                            label={`${period.label} (${periodCount})`}
                            sx={{
                              minHeight: 32,
                              minWidth: 0,
                              px: 1.25,
                              fontSize: "12px",
                              fontWeight: 600,
                              textTransform: "none",
                              color: "#8A94A6",
                              "&.Mui-selected": { color: "#48505E" },
                            }}
                          />
                        );
                      })}
                    </Tabs>
                  </Box>
                </AccordionSummary>
                <AccordionDetails sx={{ p: 2, backgroundColor: "#FAFAFA" }}>
                  {renderExpandedContent?.(driver, activePeriod, fetchDriverJobsPage)}
                </AccordionDetails>
              </Accordion>
            );
          })}

          {drivers.length === 0 && (
            <Typography variant="body2" color="text.secondary">
              No Jobs found.
            </Typography>
          )}
        </Box>
      )}

      {!loading && drivers.length > 0 && (
        <TablePagination
          component="div"
          count={totalDrivers}
          page={page - 1}
          onPageChange={(event, newPage) => setPage(newPage + 1)}
          rowsPerPage={pageSize}
          onRowsPerPageChange={(event) => {
            setPageSize(parseInt(event.target.value, 10));
            setPage(1);
          }}
          rowsPerPageOptions={[20, 50, 100]}
        />
      )}
    </Box>
  );
};

export default DriversPanel;
